import { faGear, faSignOut, faUser } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import type { Sport } from '__generated__/globalTypes';
import { Dropdown } from 'atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from 'atoms/icons';
import { StarBall } from 'atoms/icons/StarBall';
import { Horizontal, SBHorizontal, Vertical } from 'atoms/layout/flex';
import { BodyL, BodyS } from 'atoms/typography';
import { Badge } from 'atoms/ui/Badge';
import { GoProLink } from 'components/GoProLink';
import { CloseButton } from 'components/dialog/CloseButton';
import { MoreNavItems } from 'components/navigation/AppNavigation/NavItems/MoreNavItems';
import { Footer } from 'components/navigation/AppNavigation/NavItems/MoreNavItems/Footer';
import { SportSelector } from 'components/navigation/AppNavigation/SportSelector';
import { MarketCredits } from 'components/navigation/AppNavigation/UserCurrencies/MarketCredits';
import { MoneyBalances } from 'components/navigation/AppNavigation/UserCurrencies/MoneyBalances';
import { NavItem, Separator } from 'components/navigation/NavItem';
import {
  ANY_SPORT_MY_CLUB,
  MY_SORARE,
  SETTINGS,
} from 'constants/__generated__/routes';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useIntlContext } from 'contexts/intl';
import { useWalletContext } from 'contexts/wallet';
import { useWalletDrawerContext } from 'contexts/walletDrawer';
import { useSimplifiedNav } from 'hooks/commonGame/useSimplifiedNav';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { useCurrentUserCurrencies } from 'hooks/useCurrentUserCurrencies';
import { useInviteLink } from 'hooks/useInviteLink';
import { useReferralProgramConfig } from 'hooks/useReferralProgramConfig';
import { glossary, navLabels } from 'lib/glossary';
import { generateSportPath } from 'lib/routing/generateSportPath';
import { laptopAndAbove } from 'style/mediaQuery';
import { regularMaterial } from 'style/utils';

import { MyClubNavItem } from './MyClubNavItem';
import { ProfileButtonContent } from './ProfileButtonContent';

const DropdownContent = styled(Vertical).attrs({ gap: 2 })`
  top: 0;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  ${regularMaterial}

  @media ${laptopAndAbove} {
    width: 375px;
  }
`;

const GoProLinkWrapper = styled.div`
  height: 120px;
  display: flex;
`;

const StyledSBHorizontal = styled(SBHorizontal)`
  width: 100%;
`;

const Box = styled(Link)`
  display: flex;
  gap: var(--double-unit);
  align-items: flex-start;
  flex-direction: column;
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--double-unit);
  flex-grow: 1;
  align-self: stretch;

  &:hover {
    background-color: rgba(var(--c-rgb-white), 0.04);
  }
`;

type Props = { sport: Sport };
export const UserNav = ({ sport }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const inviteLink = useInviteLink();
  const { showDrawer } = useWalletDrawerContext();
  const { formatMessage } = useIntl();
  const isLaptopAndAbove = useIsLaptop();
  const walletContext = useWalletContext();
  const { showSimplifiedNav } = useSimplifiedNav(sport);
  const { fiatCurrency } = useCurrentUserCurrencies();
  const { userFiatAmount } = useReferralProgramConfig(sport);
  const { formatNumber } = useIntlContext();

  const amount =
    userFiatAmount &&
    formatNumber(userFiatAmount, {
      style: 'currency',
      currency: fiatCurrency,
      maximumFractionDigits: 0,
    });

  return (
    <Dropdown
      transparent
      forceDesktopBehavior={isLaptopAndAbove}
      label={({ onClick, open }) => (
        <button type="button" onClick={onClick}>
          <ProfileButtonContent sport={sport} active={open} />
        </button>
      )}
      side={isLaptopAndAbove ? 'bottom' : 'left'}
      gap={16}
    >
      {({ closeDropdown }) => (
        <DropdownContent>
          {!isLaptopAndAbove && (
            <SBHorizontal>
              <SportSelector displaySelectedSportName />
              <CloseButton onClose={closeDropdown} />
            </SBHorizontal>
          )}
          <MyClubNavItem
            to={generateSportPath(ANY_SPORT_MY_CLUB, { sport })}
            onClick={closeDropdown}
          />
          {!!showSimplifiedNav && (
            <GoProLinkWrapper>
              <GoProLink />
            </GoProLinkWrapper>
          )}
          <Horizontal>
            <Box to={MY_SORARE}>
              <StarBall width={16} height={16} />
              <Vertical gap={0.5}>
                <BodyL bold>{formatMessage(navLabels.mySorare)}</BodyL>
                <BodyS color="var(--c-nd-600)">
                  <FormattedMessage
                    id="Nav.mySorare.seeWhatsnew"
                    defaultMessage="See what’s new"
                  />
                </BodyS>
              </Vertical>
            </Box>
            <Box to={inviteLink}>
              <StyledSBHorizontal>
                <FontAwesomeIcon icon={faUser} />
                <Badge
                  badgeContent={currentUser?.unclaimedReferralRewardsCount}
                  right={1}
                />
              </StyledSBHorizontal>
              <Vertical gap={0.5}>
                <BodyL bold>{formatMessage(navLabels.inviteFriends)}</BodyL>
                <BodyS color="var(--c-nd-600)">
                  <FormattedMessage
                    id="Nav.mySorare.earnUpTo"
                    defaultMessage="Earn up to {amount}"
                    values={{ amount }}
                  />
                </BodyS>
              </Vertical>
            </Box>
          </Horizontal>
          {!isLaptopAndAbove && (
            <>
              <MoneyBalances onClick={showDrawer} />
              <button onClick={showDrawer} type="button">
                <MarketCredits sport={sport} />
              </button>
            </>
          )}
          <Vertical>
            <NavItem
              icon={() => <FontAwesomeIcon icon={faGear} />}
              label={formatMessage(navLabels.settings)}
              to={SETTINGS}
            />
            {!isLaptopAndAbove && <MoreNavItems noFooter />}
            {!showSimplifiedNav && <Separator />}
            <NavItem
              icon={() => <FontAwesomeIcon icon={faSignOut} />}
              label={formatMessage(glossary.logOut)}
              onClick={() => {
                walletContext.logOut();
                closeDropdown();
              }}
            />
            {!isLaptopAndAbove && (
              <>
                <Separator />
                <Footer />
              </>
            )}
          </Vertical>
        </DropdownContent>
      )}
    </Dropdown>
  );
};
